/**
 * How hard a part plays through, as data: five levels from piano alone to the full band.
 *
 * A leaf, like chordRhythmPatterns.js. It knows levels and how loud each hand plays at one; what the
 * drums do at each level is in drumGrooves.js, beside the pieces it names.
 */

import i18n from '../i18n';

export const SPARSE = 1;
export const SOFT = 2;
export const NORMAL = 3;
export const STRONG = 4;
export const FULL = 5;

/**
 * `piano` and `bass` scale every note's velocity. `bass: 0` sends the bass player out, and the
 * piano's left hand comes back. `double` adds the octave above the top of every chord, and of the pad.
 * `pad` is the pad's velocity, 0 for out, on a style that has one, and `brass`, `guitar` and
 * `acoustic` the same for each of the band's extra players (chordBand.js), and `violin`, which leaves the
 * quiet levels to the piano.
 * `thin` drops the soft answers from the piano's figure and the bass line, keeping the accents
 * (getAccentSteps).
 * `padBed` is the pad's gain where it is kept on under a quieter part (PAD_KEEP), rather than
 * leaving with the band. Sparse leaves it out unless the part itself says keep.
 */
export const INTENSITY_LEVELS = [
  { id: SPARSE, label: 'Sparse', piano: 0.6, bass: 0, pad: 0, padBed: 0.25, brass: 0, guitar: 0, acoustic: 0, violin: 0, thin: true },
  { id: SOFT, label: 'Soft', piano: 0.8, bass: 0.8, pad: 0, padBed: 0.32, brass: 0, guitar: 0, acoustic: 0.8, violin: 0, thin: true },
  { id: NORMAL, label: 'Normal', piano: 1, bass: 1, pad: 0, padBed: 0.5, brass: 0, guitar: 0, acoustic: 1, violin: 0.85 },
  { id: STRONG, label: 'Strong', piano: 1.1, bass: 1.1, pad: 0.8, padBed: 1, brass: 0.8, guitar: 0, acoustic: 0, violin: 0.95 },
  { id: FULL, label: 'Full', piano: 1.2, bass: 1.2, pad: 1, padBed: 1, brass: 1, guitar: 1, acoustic: 0, violin: 1, double: true },
];

const clampLevel = (level) => Math.min(FULL, Math.max(SPARSE, Math.round(level)));

/** One level by id; anything unset or unreadable is Normal, which is how a chart has always played. */
export const getIntensityLevel = (id) =>
  INTENSITY_LEVELS.find((level) => level.id === Number(id)) || INTENSITY_LEVELS[NORMAL - 1];

/** A level's name in the app's language; `label` stays the English one. */
export const getIntensityLabel = (id) => {
  const { label } = getIntensityLevel(id);

  return i18n.t(`intensity.levels.${label.toLowerCase()}`, { defaultValue: label });
};

/**
 * The level a part plays one of its passes at. A level set for the pass wins; else the part's own,
 * one up on its last pass where it builds — a setting songs saved before the per-pass levels still carry.
 *
 * @param {object} part - `{ intensity, intensityPasses, build, repeatCount }`
 * @param {number} pass - 1-based, counted against the part's repeatCount
 */
export const getPassIntensity = ({ intensity, intensityPasses, build, repeatCount } = {}, pass = 1) => {
  const set = intensityPasses?.[pass - 1];

  if (set) return clampLevel(set);

  const level = Number(intensity) || NORMAL;
  const last = Math.max(1, Math.round(repeatCount) || 1);

  return clampLevel(build && last > 1 && pass === last ? level + 1 : level);
};

/** A part's intensity fields as a run entry carries them. */
export const toIntensityEntry = (part) => ({
  intensity: part?.chartIntensity || NORMAL,
  intensityPasses: part?.chartIntensityPasses || null,
  build: Boolean(part?.chartBuild),
  fill: part?.chartFill !== false,
  drop: Boolean(part?.chartDrop),
  pad: part?.chartPad || null,
  layers: part?.chartLayers || null,
});

/** Whether an entry is arranged at all: anything in it that plays other than plain Normal. */
export const hasIntensity = (entry = {}) =>
  (Number(entry.intensity) || NORMAL) !== NORMAL ||
  Boolean(entry.intensityPasses?.some(Boolean)) ||
  Boolean(entry.build || entry.drop || entry.pad || entry.padMode) ||
  entry.fill === false ||
  Object.values(entry.layers || {}).some(Boolean);

/** An entry played as if no intensity had been set: Normal throughout, with only the players its style brings. */
export const withoutIntensity = (entry) => ({
  ...entry,
  intensity: NORMAL,
  intensityPasses: null,
  build: false,
  fill: true,
  drop: false,
  pad: null,
  padMode: null,
  layers: null,
});

/** The players a part can switch on or off for itself in `chartLayers`, by id. */
export const PLAYER_IDS = ['bass', 'brass', 'guitar', 'acoustic', 'violin', 'drums'];
/** `chartLayers[id]`: the player is in on the part at every level, where its style has it. */
export const LAYER_ON = 'on';
/** `chartLayers[id]`: the player is out of the part at every level. */
export const LAYER_OFF = 'off';

/**
 * How loud a player plays at a level, as its velocity scale, 0 for out. As the level has it unless
 * the part switches it; switched on at a level without it, it plays as loud as the piano does there.
 *
 * @param {object} layers - A part's `chartLayers`, or nothing
 */
export const getLayerGain = (layers, id, intensity = NORMAL) => {
  const level = getIntensityLevel(intensity);

  if (layers?.[id] === LAYER_OFF) return 0;
  if (layers?.[id] === LAYER_ON) return level[id] || level.piano;

  return level[id] || 0;
};

/** `chartPad`: the pad stays on from this part, under the quieter parts too, until one cuts it. */
export const PAD_KEEP = 'keep';
/** `chartPad`: the pad goes from this part, at every level, until one keeps it. */
export const PAD_CUT = 'cut';

/**
 * Each entry tagged with `padMode`, the last `pad` set at or before it: PAD_KEEP, PAD_CUT, or null
 * where none has been, which brings the pad in with the level as always. Carried across the whole
 * song, so read before a run is cut down to start from a later part. A Section (`section`) starts
 * the next song of a mix afresh.
 */
export const carryPadModes = (entries) => {
  let mode = null;

  return entries.map((entry) => {
    if (entry.section) mode = null;
    if (entry.pad === PAD_KEEP || entry.pad === PAD_CUT) mode = entry.pad;

    return { ...entry, padMode: mode };
  });
};

/**
 * How loud the pad plays at a pass, as a gain on it, 0 for out. As the level has it unless kept or
 * cut; kept, the quieter levels hold it at their `padBed`, and Sparse only where the part says keep.
 *
 * @param {object} entry - `{ pad, padMode }`, as carryPadModes leaves it
 */
export const getPadGain = ({ pad, padMode } = {}, intensity = NORMAL) => {
  const level = getIntensityLevel(intensity);

  if (padMode === PAD_CUT) return 0;
  if (padMode !== PAD_KEEP) return level.pad > 0 ? 1 : 0;
  if (level.id === SPARSE && pad !== PAD_KEEP) return 0;

  return level.padBed;
};

/** How many beats of silence a drop leaves before a part: one, or a dotted quarter in 6/8. */
export const getDropBeats = (beatsPerBar) => (Number(beatsPerBar) === 6 ? 3 : 1);
